import { useCallback, useState } from 'react';

import { Box, Button, Col, Dialog, PressableBox, Row, Text } from '@opendoor/bricks-next';
import type { SavedSearchFieldsFragment } from '__generated__/athena';
import { useRouter } from 'next/router';

import { useObservability } from '../../../helpers/observability';
import { buildQueryParams } from '../helpers/useSearchQueries';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { SAVED_SEARCHES_ENABLED } from './featureFlag';
import { toPropertySearchQuery } from './mapping';
import { RenameSavedSearchDialog } from './RenameSavedSearchDialog';
import { useSavedSearches } from './useSavedSearches';

const DEFAULT_MARKET_FALLBACK = 'phoenix';

// Canonical market slugs are lowercase tokens joined by hyphens/underscores
// (`phoenix`, `las-vegas`). Reject anything else — most importantly the
// display labels (`Downtown Phoenix`) that pre-canonicalization or
// cross-client saved searches may have stored in `location.market` — so we
// never navigate to a non-routable `/homes/<label>` that errors during SSR
// market resolution (Sentry COSMOS-J0X). zoneIds/vertices still drive the
// result set via the query string, so the default-market base is safe.
const CANONICAL_MARKET_SLUG = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;

// Mirrors the marketplace path conventions in `src/pages/homes/[market].tsx`:
//   single-zone   -> /homes/zid_<id>
//   market-only   -> /homes/<market>
//   multi-zone    -> /homes/<market or fallback>?zoneIds=...
export const pickPathname = (savedSearch: SavedSearchFieldsFragment): string => {
  const { zoneIds, market } = savedSearch.location;
  if (zoneIds.length === 1) {
    return `/homes/zid_${zoneIds[0]}`;
  }
  if (market && CANONICAL_MARKET_SLUG.test(market)) {
    return `/homes/${market}`;
  }
  return `/homes/${DEFAULT_MARKET_FALLBACK}`;
};

export interface SavedSearchesDropdownProps {
  /**
   * Optional size override for the trigger button. Defaults to "small" to
   * match `QuickFilters` controls.
   */
  size?: 'xsmall' | 'small' | 'medium';
}

export const SavedSearchesDropdown = ({ size = 'small' }: SavedSearchesDropdownProps) => {
  const router = useRouter();
  const { trackEvent } = useObservability();
  const { isLoggedIn, savedSearches } = useSavedSearches();

  const [isOpen, setIsOpen] = useState(false);
  const [pendingRename, setPendingRename] = useState<SavedSearchFieldsFragment | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SavedSearchFieldsFragment | null>(null);

  const handleSelect = useCallback(
    (savedSearch: SavedSearchFieldsFragment) => {
      const query = toPropertySearchQuery(savedSearch);
      const pathname = pickPathname(savedSearch);
      // `buildQueryParams` reaches into `value.forEach` for the `vertices` key,
      // which crashes on `undefined`. `toPropertySearchQuery` leaves fields
      // unset as `undefined` rather than omitting them, so we strip undefineds
      // before delegating to keep the helper unchanged.
      const definedQuery = Object.fromEntries(
        Object.entries(query).filter(([, value]) => value !== undefined),
      );
      // Don't seed from the current URL: a saved search must produce its own
      // canonical query, otherwise stale params from the page we're leaving
      // (e.g. the user's current filters) would carry into the destination.
      const urlParams = buildQueryParams(definedQuery, { seedFromUrl: false });
      const queryRecord: Record<string, string> = {};
      urlParams.forEach((value: string, key: string) => {
        queryRecord[key] = value;
      });
      trackEvent('cta-click', 'saved-search-selected', {
        savedSearchId: savedSearch.id,
        market: savedSearch.location.market,
      });
      // Use `router.push` (not `replace`) so users can hit Back to undo the
      // switch. The SSR + URL-driven `useSearchState` will re-hydrate the
      // page from the new params on transition.
      router.push({ pathname, query: queryRecord });
      setIsOpen(false);
    },
    [router, trackEvent],
  );

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleOpenRename = useCallback((savedSearch: SavedSearchFieldsFragment) => {
    setPendingRename(savedSearch);
  }, []);

  const handleOpenDelete = useCallback((savedSearch: SavedSearchFieldsFragment) => {
    setPendingDelete(savedSearch);
  }, []);

  const handleCloseRename = useCallback(() => {
    setPendingRename(null);
  }, []);

  const handleCloseDelete = useCallback(() => {
    setPendingDelete(null);
  }, []);

  if (!SAVED_SEARCHES_ENABLED || !isLoggedIn || savedSearches.length === 0) {
    return null;
  }

  const buttonLabel = `Saved searches (${savedSearches.length})`;

  return (
    <>
      <Button
        analyticsName="saved-searches-dropdown-trigger"
        label={buttonLabel}
        onPress={handleOpen}
        size={size}
        variant="secondary"
      />
      <Dialog
        analyticsName="saved-searches-dropdown-dialog"
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        dismissable
      >
        <Box width="100%" $largerThanSM={{ width: 400 }}>
          <Dialog.Content
            title="Your saved searches"
            description="Switch to a saved search, or manage your saved searches inline."
            hideDescription
          >
            <Col pt={8} pb={4} gap={4}>
              {savedSearches.map((savedSearch) => (
                <SavedSearchRow
                  key={savedSearch.id}
                  savedSearch={savedSearch}
                  onSelect={handleSelect}
                  onRename={handleOpenRename}
                  onDelete={handleOpenDelete}
                />
              ))}
            </Col>
          </Dialog.Content>
        </Box>
      </Dialog>
      {pendingRename && (
        <RenameSavedSearchDialog
          savedSearch={pendingRename}
          isOpen={pendingRename !== null}
          onClose={handleCloseRename}
        />
      )}
      {pendingDelete && (
        <ConfirmDeleteDialog
          savedSearch={pendingDelete}
          isOpen={pendingDelete !== null}
          onClose={handleCloseDelete}
        />
      )}
    </>
  );
};

interface SavedSearchRowProps {
  savedSearch: SavedSearchFieldsFragment;
  onSelect: (savedSearch: SavedSearchFieldsFragment) => void;
  onRename: (savedSearch: SavedSearchFieldsFragment) => void;
  onDelete: (savedSearch: SavedSearchFieldsFragment) => void;
}

const SavedSearchRow = ({ savedSearch, onSelect, onRename, onDelete }: SavedSearchRowProps) => {
  const handleSelect = useCallback(() => {
    onSelect(savedSearch);
  }, [onSelect, savedSearch]);

  const handleRename = useCallback(() => {
    onRename(savedSearch);
  }, [onRename, savedSearch]);

  const handleDelete = useCallback(() => {
    onDelete(savedSearch);
  }, [onDelete, savedSearch]);

  return (
    <Row
      alignItems="center"
      justifyContent="space-between"
      borderBottomWidth={1}
      borderBottomColor="$borderSecondary"
      py={12}
      gap={12}
    >
      <PressableBox
        flex={1}
        analyticsName="saved-search-select"
        aria-label={`Switch to saved search ${savedSearch.name}`}
        onPress={handleSelect}
        py={4}
      >
        <Text tag="p" typography="$bodyMedium" numberOfLines={1}>
          {savedSearch.name}
        </Text>
      </PressableBox>
      <Row gap={8}>
        <Button
          analyticsName="saved-search-rename"
          label="Rename"
          variant="secondary"
          size="xsmall"
          onPress={handleRename}
        />
        <Button
          analyticsName="saved-search-delete"
          label="Delete"
          variant="secondary"
          size="xsmall"
          onPress={handleDelete}
        />
      </Row>
    </Row>
  );
};
